import React, { FC } from 'react'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ICarrierScorecard } from 'types/FMSGraphqlTypes'
import { InfoIcon, LockedIcon } from 'transfix-icons'
import { StatsBox } from './StatsBox'

const useCarrierRatingStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: theme.spacing(4),
    background: brandColors.coolGray1,

    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(2),
    },
  },
  contentTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    marginBottom: 24,

    [theme.breakpoints.down('lg')]: {
      alignItems: 'flex-start',
      marginBottom: 16,
    },

    '& p': {
      [theme.breakpoints.down('lg')]: {
        fontSize: 16,
      },
    },

    '& svg': {
      width: '24px',
      height: '24px',

      [theme.breakpoints.down('lg')]: {
        marginTop: 6,
      },
    },
  },
  boxContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: 24,

    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      gap: 12,
    },
  },
}))

interface IStatisticsSectionProps {
  carrierScorecard: ICarrierScorecard | null
  getMetric: (rate: number, variant: string) => string
}

export const StatisticsSection: FC<IStatisticsSectionProps> = ({ carrierScorecard, getMetric }) => {
  const classes = useCarrierRatingStyles()
  const {
    bounceFailure,
    bouncePct,
    bounceSuccess,
    onTimeFailure,
    onTimePct,
    onTimeSuccess,
    stale: isStale = false,
    trackingFailure,
    trackingPct,
    trackingSuccess,
  } = carrierScorecard || {}

  return (
    <div className={classes.container}>
      {!carrierScorecard ||
        (isStale && (
          <div className={classes.contentTitle}>
            <LockedIcon />
            <Typography variant='body1' component='p'>
              Rating category breakdown will be visible after you run your first load.{' '}
              <a href='/fms/book'>Find Loads now!</a>
            </Typography>
          </div>
        ))}

      <div className={classes.boxContainer}>
        <StatsBox
          getMetric={getMetric}
          variant='onTimeRate'
          rate={onTimePct}
          failure={onTimeFailure || 0}
          success={onTimeSuccess || 0}
          isStale={isStale}
        />
        <StatsBox
          getMetric={getMetric}
          variant='trackingQuality'
          rate={trackingPct}
          failure={trackingFailure || 0}
          success={trackingSuccess || 0}
          isStale={isStale}
        />
        <StatsBox
          getMetric={getMetric}
          variant='lateCancellation'
          rate={bouncePct || undefined}
          failure={bounceFailure || 0}
          success={bounceSuccess || 0}
          isStale={isStale}
        />
      </div>

      <div className={classes.contentTitle}>
        <InfoIcon />
        <Typography variant='body1' component='p'>
          All ratings are based on your{' '}
          <strong>most recent 50 Transfix loads within the past year</strong>. As you complete new
          loads your rating will update accordingly.
        </Typography>
      </div>
    </div>
  )
}
